import React, { useState, useEffect, useCallback } from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { useAuth0 } from "@auth0/auth0-react";
import { getMyActivityLog } from "./request/profileApi";

interface LogEntry {
    date: string;
    type: string;
    description: string;
    ip: string;
    user_agent: string;
    log_id: string;
}

const EVENT_TYPE_VARIANTS: Record<string, "success" | "error" | "warning" | "default"> = {
    s: "success",
    ss: "success",
    f: "error",
    fp: "error",
    fu: "error",
    w: "warning",
    du: "default",
    sv: "success",
    fv: "error",
};

const EVENT_TYPE_LABELS: Record<string, string> = {
    s: "Login Success",
    ss: "Signup",
    seacft: "Token Exchange",
    f: "Login Failed",
    fp: "Wrong Password",
    fu: "Invalid Email",
    fsa: "Failed Silent Auth",
    ssa: "Silent Auth Success",
    slo: "Logout Success",
    flo: "Logout Failed",
    scoa: "Code/Link Auth",
    sv: "Email Verified",
    fv: "Verification Failed",
    scp: "Password Changed",
    fcp: "Password Change Failed",
    du: "User Deleted",
    gd_otp_rate_limit_exceed: "OTP Rate Limit",
    gd_enrollment_complete: "MFA Enrolled",
    gd_auth_succeed: "MFA Auth Success",
    gd_auth_failed: "MFA Auth Failed",
};

const PER_PAGE = 50;

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString();
}

function parseUserAgent(ua: string): string {
    if (!ua) return "Unknown";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    return ua.substring(0, 30) + (ua.length > 30 ? "..." : "");
}

const activityColumns: GenericTableColumn<LogEntry>[] = [
    {
        header: "Date",
        renderCell: (row) => formatDate(row.date),
        wrapMode: "nowrap",
    },
    {
        header: "Event",
        renderCell: (row) => (
            <GenericTag
                label={EVENT_TYPE_LABELS[row.type] || row.type}
                variant={EVENT_TYPE_VARIANTS[row.type] || "default"}
                size="small"
            />
        ),
    },
    {
        header: "IP Address",
        renderCell: (row) => (
            <span className="font-mono text-xs">{row.ip || "-"}</span>
        ),
    },
    {
        header: "Browser",
        renderCell: (row) => parseUserAgent(row.user_agent),
    },
];

const ActivityTab: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();

    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadLogs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await getAccessTokenSilently();
            const response = await getMyActivityLog(token, 0, PER_PAGE);

            const data = response.data;
            const logEntries: LogEntry[] = Array.isArray(data.logs)
                ? data.logs
                : Array.isArray(data)
                ? data
                : [];

            setLogs(logEntries);
        } catch {
            setError("Error loading activity log.");
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    if (loading && logs.length === 0) {
        return <TableSkeleton columns={4} rows={8} />;
    }

    if (error) {
        return (
            <div className="text-center py-8">
                <p className="text-semantic-text-secondary text-sm">{error}</p>
                <GenericButton
                    label="Retry"
                    variant="secondary"
                    size="sm"
                    onClick={() => loadLogs()}
                    className="mt-3"
                />
            </div>
        );
    }

    if (logs.length === 0) {
        return (
            <div className="text-center py-12 text-semantic-text-secondary text-sm border border-dashed border-semantic-border-secondary rounded-md">
                No activity log entries found.
            </div>
        );
    }

    return (
        <GenericTable
            columns={activityColumns}
            data={logs}
            rowKey={(row, idx) => row.log_id || idx}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
            initialRowsPerPage={15}
            rowsPerPageOptions={[15, 25, 50]}
            maxTableBodyHeight="600px"
            overflow="hidden"
        />
    );
};

export default ActivityTab;
